import React, { useEffect, useState } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend } from "recharts";
import { LineChart as LineIcon, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Panel } from "./ui";
import { api } from "../lib/api";
import { timeAxis } from "../lib/chartAxis";
import { shortDate } from "../lib/format";

const FALLBACK_OPTIONS = [
  { value: "sp500", label: "S&P 500" }, { value: "nasdaq", label: "Nasdaq" },
  { value: "dax", label: "DAX" }, { value: "bitcoin", label: "Bitcoin" }, { value: "gold", label: "Gold" },
];

// Optional, collapsed-by-default panel: indexes the instrument's close price and a
// chosen benchmark to 100 at the period start so relative performance is comparable
// regardless of the instrument's absolute price. Mirrors Analytics' portfolio-vs-benchmark chart.
export default function BenchmarkCompare({ ticker, range }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [symbol, setSymbol] = useState("sp500");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.instrumentBenchmark(ticker, range, symbol).then((r) => setData(r.data)).catch(() => setData(null));
  }, [open, ticker, range, symbol]);
  // Same range, same candles and the same labels as the price chart above it.
  const time = timeAxis(data?.series, data?.intraday, { dailyTick: shortDate });

  return (
    <div>
      <button data-testid="toggle-benchmark" onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-700 transition-colors">
        <LineIcon size={15} /> {t("instrument.compareBenchmark")}
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <Panel className="card p-4 sm:p-6 mt-3" testid="instrument-benchmark">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className="font-heading font-semibold tracking-tight text-lg">
              {t("instrument.vsBenchmark", { ticker, benchmark: data?.benchmark_symbol || "S&P 500" })}
            </h3>
            <select data-testid="instrument-benchmark-select" value={symbol} onChange={(e) => setSymbol(e.target.value)}
              className="input py-1.5 w-full sm:w-auto text-sm font-medium">
              {(data?.options || FALLBACK_OPTIONS).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          {!data || data.series.length === 0 ? (
            <div className="h-[240px] flex items-center justify-center text-slate-400 text-sm">{t("analytics.notEnoughHistory")}</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={data.series} margin={{ top: 6, right: 6, left: -10, bottom: 0 }}>
                <XAxis dataKey="date" {...time.axis} tick={{ fontSize: 11, fill: "#94a3b8" }} minTickGap={50} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={48} tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={(v, n) => [v == null ? "—" : `${v > 0 ? "+" : ""}${v}%`, n]} {...time.tooltip} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="linear" dataKey="instrument" name={ticker} stroke="#0052FF" strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
                <Line type="linear" dataKey="benchmark" name={data.benchmark_symbol} stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="4 3" dot={false} isAnimationActive={false} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Panel>
      )}
    </div>
  );
}
